import { useCallback, useRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import heic2any from 'heic2any'
import moment from 'moment'
import { api, getErrorAttributes } from 'lib/axios'
import { getUserId } from 'lib/auth/tokenStorage'
import { collectDroppedFileNames, generateUUID } from 'utils/helpers'
import { isAmexRefundCard } from 'utils/arn'

const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/heif',
  'image/heic',
  'application/pdf'
]

const isSpreadsheet = file => {
  const name = (file?.name || '').toLowerCase()
  return ['.xlsx', '.xls'].some(ext => name.endsWith(ext))
}

const isAllowedArnPasteFile = file => {
  if (!file) return false
  if (isSpreadsheet(file)) return false
  if (!file.type && !file.name) return false

  return (
    ALLOWED_TYPES.includes(file.type) ||
    (file.name || '')
      .toLowerCase()
      .match(/\.(png|jpe?g|webp|heif|heic|pdf)$/)
  )
}

const convertHeif = async file => {
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpg'
    })
    return new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
      type: 'image/jpg'
    })
  } catch (error) {
    console.error('ARN paste HEIF conversion error:', error)
    return file
  }
}

export const getClipboardFile = event => {
  const clipboardItems = event?.clipboardData?.items || []
  const clipboardFiles = event?.clipboardData?.files || []

  for (const item of clipboardItems) {
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) return file
    }
  }
  if (clipboardFiles.length > 0) return clipboardFiles[0]
  return null
}

const normalizeName = n => (typeof n === 'string' ? n.trim().toLowerCase() : '')
const stripExt = n => n.replace(/\.[^.\\/]+$/, '')

const isDuplicatePastedFile = (file, customerFiles, reduxState) => {
  const pastedFileName = file?.name || ''
  const pastedNorm = normalizeName(pastedFileName)
  const pastedBase = stripExt(pastedNorm)

  const collectFull = new Set()
  const collectBase = new Set()

    ; (customerFiles || []).forEach(f => {
      const raw =
        f?.file_obj?.name ?? f?.file_obj?.file_name ?? f?.fileName ?? ''
      const norm = normalizeName(raw)
      if (!norm) return
      collectFull.add(norm)
      const base = stripExt(norm)
      if (base) collectBase.add(base)
    })

  try {
    collectDroppedFileNames(reduxState).forEach(raw => {
      const norm = normalizeName(raw)
      if (!norm) return
      collectFull.add(norm)
      const base = stripExt(norm)
      if (base) collectBase.add(base)
    })
  } catch (_) {
    // fall back to customer_files-only set
  }

  return (
    (!!pastedNorm && collectFull.has(pastedNorm)) ||
    (!!pastedBase && collectBase.has(pastedBase))
  )
}

export const useArnSlotPaste = () => {
  const customer = useSelector(state => state?.customer)
  const customerFiles = useSelector(state => state?.customer_files?.files)
  const paymentCards = useSelector(state => state?.paymentCards?.paymentCards)
  const store = useStore()
  const isAmexRefund = isAmexRefundCard(paymentCards)
  const [isPasting, setIsPasting] = useState(false)
  const [duplicateImagesFiles, setDuplicateImagesFiles] = useState([])
  const [duplicateImagesModal, setDuplicateImagesModal] = useState(false)
  const [disAllowedFiles, setDisAllowedFiles] = useState([])
  const [disallowedImagesModal, setDisallowedImagesModal] = useState(false)
  const isPastingRef = useRef(false)
  const agentId = getUserId()

  const closeDuplicateModal = useCallback(() => {
    setDuplicateImagesModal(false)
    setDuplicateImagesFiles([])
  }, [])

  const closeDisallowedModal = useCallback(() => {
    setDisallowedImagesModal(false)
    setDisAllowedFiles([])
  }, [])

  const trackArnActivity = useCallback(
    async slotIndex => {
      try {
        const description =
          isAmexRefund && (slotIndex === 0 || slotIndex === 1)
            ? `uploaded ARN ${slotIndex + 1}`
            : null

        await api.post('/Api/V8/module', {
          data: {
            type: 'gb_action_tracking_logs',
            attributes: {
              affiliate_member_id: agentId,
              action: 'upload_arn',
              action_time: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
              parent_id: customer.id,
              ...(description ? { description } : {})
            }
          }
        })
      } catch (error) {
        console.error('Error tracking ARN activity:', error)
      }
    },
    [agentId, customer.id, isAmexRefund]
  )

  const uploadPastedArnFile = useCallback(
    async file => {
      if (!file || isSpreadsheet(file)) return null
      if (!file.type && !file.name) return null

      let uploadFile = file
      if (
        file.type === 'image/heif' ||
        file.type === 'image/heic' ||
        (file.name || '').toLowerCase().match(/\.(heif|heic)$/)
      ) {
        uploadFile = await convertHeif(file)
      }

      const typeOk =
        ALLOWED_TYPES.includes(uploadFile.type) ||
        (uploadFile.name || '').toLowerCase().match(/\.(png|jpe?g|webp|heif|heic|pdf)$/)
      if (!typeOk) return null

      const fileExtension = (uploadFile.type || '').split('/')[1] || 'png'
      const hasOriginalName =
        !!uploadFile.name && uploadFile.name.toLowerCase() !== 'image.png'
      const updatedFileName = hasOriginalName
        ? uploadFile.name
        : `ARN_DOC_${Math.floor(10000 + Math.random() * 90000)}.${fileExtension}`

      const namedFile = new File([uploadFile], updatedFileName, {
        type: uploadFile.type,
        lastModified: uploadFile.lastModified
      })

      const formData = new FormData()
      formData.append('images[]', namedFile)
      formData.append('contact_id', customer.id)
      formData.append('agent_id', agentId)

      const response = await api.post(
        '/Api/V8/custom/customer/process-all-files',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000
        }
      )

      if (response.status === 200 && response.data?.length > 0) {
        return { file_obj: { ...response.data[0] }, id: generateUUID() }
      }
      return null
    },
    [agentId, customer.id]
  )

  const handleCardPaste = useCallback(
    async (event, onUploaded, slotIndex = null) => {
      event.preventDefault()
      event.stopPropagation()
      if (isPastingRef.current) return

      const pastedFile = getClipboardFile(event)
      if (!pastedFile) return

      if (!isAllowedArnPasteFile(pastedFile)) {
        setDisAllowedFiles([pastedFile])
        setDisallowedImagesModal(true)
        return
      }

      if (
        isDuplicatePastedFile(pastedFile, customerFiles, store.getState())
      ) {
        setDuplicateImagesFiles([
          {
            file_no: 0,
            file: pastedFile,
            id: generateUUID(),
            fileName: pastedFile.name || '',
            status: 'Waiting for upload...',
            progress: 0,
            isUploaded: true,
            file_obj: {
              id: '',
              document_revision_id: '',
              gb_doc_type: null,
              name: '',
              rotate_angle_c: null
            }
          }
        ])
        setDuplicateImagesModal(true)
        return
      }

      isPastingRef.current = true
      setIsPasting(true)
      try {
        const uploaded = await uploadPastedArnFile(pastedFile)
        if (uploaded && typeof onUploaded === 'function') {
          await onUploaded(uploaded)
          await trackArnActivity(slotIndex)
        }
      } catch (error) {
        const { message } = getErrorAttributes(error)
        console.error('ARN paste upload failed:', message || error)
      } finally {
        isPastingRef.current = false
        setIsPasting(false)
      }
    },
    [customerFiles, store, trackArnActivity, uploadPastedArnFile]
  )

  return {
    isPasting,
    handleCardPaste,
    uploadPastedArnFile,
    duplicateImagesModal,
    duplicateImagesFiles,
    closeDuplicateModal,
    disallowedImagesModal,
    disAllowedFiles,
    closeDisallowedModal
  }
}
